/**
 * DateTimeRangePicker - DateTime range selection component
 */

import DateRangePicker from "./DateRangePicker.js";
import TimePicker from "./TimePicker.js";
import NepaliCalendar from "../calendar/NepaliCalendar.js";
import EnglishCalendar from "../calendar/EnglishCalendar.js";

class DateTimeRangePicker {
  constructor(options = {}) {
    this.options = {
      calendar: "nepali", // 'nepali' or 'english'
      language: "en", // 'en' or 'ne'
      dateFormat: "YYYY-MM-DD", // Date format
      timeFormat: "12", // '12' or '24'
      format: "YYYY-MM-DD HH:mm", // Combined format
      separator: " - ", // Range separator
      placeholder: "Select date & time range", // Input placeholder
      startPlaceholder: "Start", // Start placeholder
      endPlaceholder: "End", // End placeholder
      minDate: null, // Minimum selectable date
      maxDate: null, // Maximum selectable date
      minTime: null, // Minimum selectable time
      maxTime: null, // Maximum selectable time
      disabledDates: [], // Array of disabled dates
      disabledDaysOfWeek: [], // Array of disabled weekdays
      predefinedRanges: true, // Show predefined ranges
      autoApply: false, // Auto apply selection
      showInputs: true, // Show input fields
      inline: false, // Show picker inline
      onChange: null, // Callback on change
      onShow: null, // Callback on show
      onHide: null, // Callback on hide
      ...options,
    };

    this.container = null;
    this.pickerContainer = null;
    this.dateRangePicker = null;
    this.startTimePicker = null;
    this.endTimePicker = null;
    this.isVisible = false;
    this.initialized = false;
  }

  /**
   * Initialize the datetime range picker
   * @param {HTMLElement|string} element - Container element or selector
   */
  initialize(element) {
    if (typeof element === "string") {
      element = document.querySelector(element);
    }

    if (!element) {
      throw new Error("Container element not found");
    }

    this.container = element;
    this._createStructure();
    this._initializePickers();
    this._attachEventListeners();
    this.initialized = true;

    if (this.options.inline) {
      this.show();
    }
  }

  /**
   * Create picker structure
   * @private
   */
  _createStructure() {
    // Clear container
    this.container.innerHTML = "";
    this.container.classList.add("ndtp-datetime-range-picker");

    // Create input if needed
    if (this.options.showInputs) {
      this.input = document.createElement("input");
      this.input.type = "text";
      this.input.className = "ndtp-input";
      this.input.placeholder = this.options.placeholder;
      this.input.readOnly = true;
      this.container.appendChild(this.input);
    }

    // Create picker container
    this.pickerContainer = document.createElement("div");
    this.pickerContainer.className = "ndtp-picker-container";
    if (!this.options.inline) {
      this.pickerContainer.classList.add("ndtp-popup");
      this.pickerContainer.style.display = "none";
    }

    // Create tabs for date and time selection
    const tabs = this._createTabs();
    this.pickerContainer.appendChild(tabs);

    // Create content areas
    const dateRangeContainer = document.createElement("div");
    dateRangeContainer.className =
      "ndtp-date-range-container ndtp-tab-content active";
    this.pickerContainer.appendChild(dateRangeContainer);

    const timeRangeContainer = document.createElement("div");
    timeRangeContainer.className = "ndtp-time-range-container ndtp-tab-content";
    this.pickerContainer.appendChild(timeRangeContainer);

    // Create footer
    if (!this.options.autoApply) {
      const footer = this._createFooter();
      this.pickerContainer.appendChild(footer);
    }

    this.container.appendChild(this.pickerContainer);
  }

  /**
   * Create tabs
   * @private
   */
  _createTabs() {
    const tabs = document.createElement("div");
    tabs.className = "ndtp-tabs";

    const dateRangeTab = document.createElement("button");
    dateRangeTab.className = "ndtp-tab active";
    dateRangeTab.textContent = "Date Range";
    dateRangeTab.type = "button";
    dateRangeTab.onclick = () => this._switchTab("daterange");

    const timeRangeTab = document.createElement("button");
    timeRangeTab.className = "ndtp-tab";
    timeRangeTab.textContent = "Time Range";
    timeRangeTab.type = "button";
    timeRangeTab.onclick = () => this._switchTab("timerange");

    tabs.appendChild(dateRangeTab);
    tabs.appendChild(timeRangeTab);

    return tabs;
  }

  /**
   * Create footer
   * @private
   */
  _createFooter() {
    const footer = document.createElement("div");
    footer.className = "ndtp-footer";

    const cancelButton = document.createElement("button");
    cancelButton.className = "ndtp-btn ndtp-btn-cancel";
    cancelButton.textContent = "Cancel";
    cancelButton.type = "button";
    cancelButton.onclick = () => this.hide();

    const applyButton = document.createElement("button");
    applyButton.className = "ndtp-btn ndtp-btn-apply";
    applyButton.textContent = "Apply";
    applyButton.type = "button";
    applyButton.onclick = () => this._applySelection();

    footer.appendChild(cancelButton);
    footer.appendChild(applyButton);

    return footer;
  }

  /**
   * Initialize pickers
   * @private
   */
  _initializePickers() {
    const dateRangeContainer = this.pickerContainer.querySelector(
      ".ndtp-date-range-container"
    );
    const timeRangeContainer = this.pickerContainer.querySelector(
      ".ndtp-time-range-container"
    );

    // Initialize date range picker
    this.dateRangePicker = new DateRangePicker({
      calendar: this.options.calendar,
      language: this.options.language,
      format: this.options.dateFormat,
      minDate: this.options.minDate,
      maxDate: this.options.maxDate,
      disabledDates: this.options.disabledDates,
      disabledDaysOfWeek: this.options.disabledDaysOfWeek,
      predefinedRanges: this.options.predefinedRanges,
      autoApply: false,
      inline: true,
      showInputs: false,
      onChange: () => {
        this._updateValue();
      },
    });
    this.dateRangePicker.initialize(dateRangeContainer);

    // Create time pickers container
    const timePickersWrapper = document.createElement("div");
    timePickersWrapper.className = "ndtp-time-pickers-wrapper";

    // Start time
    const startTimeContainer = document.createElement("div");
    startTimeContainer.className = "ndtp-start-time";
    const startTimeLabel = document.createElement("label");
    startTimeLabel.textContent = "Start Time";
    startTimeContainer.appendChild(startTimeLabel);

    const startTimePickerContainer = document.createElement("div");
    startTimeContainer.appendChild(startTimePickerContainer);
    timePickersWrapper.appendChild(startTimeContainer);

    // End time
    const endTimeContainer = document.createElement("div");
    endTimeContainer.className = "ndtp-end-time";
    const endTimeLabel = document.createElement("label");
    endTimeLabel.textContent = "End Time";
    endTimeContainer.appendChild(endTimeLabel);

    const endTimePickerContainer = document.createElement("div");
    endTimeContainer.appendChild(endTimePickerContainer);
    timePickersWrapper.appendChild(endTimeContainer);

    timeRangeContainer.appendChild(timePickersWrapper);

    // Initialize time pickers
    this.startTimePicker = new TimePicker({
      format: this.options.timeFormat,
      language: this.options.language,
      minTime: this.options.minTime,
      onChange: () => {
        this._updateValue();
      },
    });
    this.startTimePicker.initialize(startTimePickerContainer);

    this.endTimePicker = new TimePicker({
      format: this.options.timeFormat,
      language: this.options.language,
      maxTime: this.options.maxTime,
      onChange: () => {
        this._updateValue();
      },
    });
    this.endTimePicker.initialize(endTimePickerContainer);
  }

  /**
   * Switch between tabs
   * @private
   */
  _switchTab(tab) {
    const tabs = this.pickerContainer.querySelectorAll(".ndtp-tab");
    const contents = this.pickerContainer.querySelectorAll(".ndtp-tab-content");

    tabs.forEach((t) => t.classList.remove("active"));
    contents.forEach((c) => c.classList.remove("active"));

    if (tab === "daterange") {
      tabs[0].classList.add("active");
      contents[0].classList.add("active");
    } else {
      tabs[1].classList.add("active");
      contents[1].classList.add("active");
    }
  }

  /**
   * Attach event listeners
   * @private
   */
  _attachEventListeners() {
    if (this.input && !this.options.inline) {
      this.input.addEventListener("click", () => this.show());
      this.input.addEventListener("focus", () => this.show());
    }

    // Close on outside click
    document.addEventListener("click", (e) => {
      if (!this.options.inline && this.isVisible) {
        const target = e.target;
        if (!this.container.contains(target)) {
          this.hide();
        }
      }
    });

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isVisible && !this.options.inline) {
        this.hide();
      }
    });
  }

  /**
   * Apply selection
   * @private
   */
  _applySelection() {
    this._updateValue();

    if (!this.options.inline) {
      this.hide();
    }
  }

  /**
   * Update value
   * @private
   */
  _updateValue() {
    const dateRange = this.dateRangePicker.getRange();
    const startTime = this.startTimePicker.getTime();
    const endTime = this.endTimePicker.getTime();

    if (!dateRange) return;

    const formattedValue = this._formatRange(dateRange, startTime, endTime);

    if (this.input) {
      this.input.value = formattedValue;
    }

    if (this.options.onChange) {
      this.options.onChange({
        start: {
          date: dateRange.start,
          time: startTime || { hour: 0, minute: 0 },
        },
        end: {
          date: dateRange.end,
          time: endTime || { hour: 23, minute: 59 },
        },
      });
    }
  }

  /**
   * Format range
   * @private
   */
  _formatRange(dateRange, startTime, endTime) {
    const formatDateTime = (date, time) => {
      const formattedDate = this.dateRangePicker.startCalendar.formatDate(
        date,
        this.options.dateFormat
      );
      const formattedTime = this._formatTime(time || { hour: 0, minute: 0 });
      return `${formattedDate} ${formattedTime}`;
    };

    const start = formatDateTime(dateRange.start, startTime);
    const end = formatDateTime(dateRange.end, endTime);

    return `${start}${this.options.separator}${end}`;
  }

  /**
   * Format time
   * @private
   */
  _formatTime(time) {
    let hour = time.hour;
    const minute = time.minute.toString().padStart(2, "0");
    let ampm = "";

    if (this.options.timeFormat === "12") {
      ampm = hour >= 12 ? " PM" : " AM";
      hour = hour % 12 || 12;
    }

    const formattedHour = hour.toString().padStart(2, "0");

    if (this.options.language === "ne") {
      return this._toNepaliDigits(`${formattedHour}:${minute}${ampm}`);
    }

    return `${formattedHour}:${minute}${ampm}`;
  }

  /**
   * Convert to Nepali digits
   * @private
   */
  _toNepaliDigits(text) {
    const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
    return String(text).replace(/\d/g, (digit) => nepaliDigits[digit]);
  }

  /**
   * Show the picker
   */
  show() {
    if (this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "block";
    this.isVisible = true;

    // Position the picker
    this._positionPicker();

    if (this.options.onShow) {
      this.options.onShow();
    }
  }

  /**
   * Hide the picker
   */
  hide() {
    if (!this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "none";
    this.isVisible = false;

    if (this.options.onHide) {
      this.options.onHide();
    }
  }

  /**
 * Position the picker
 * @private
 */
_positionPicker() {
    if (this.options.inline || !this.input) return;
  
    const inputRect = this.input.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Position below input by default
    let top = inputRect.bottom + scrollTop;
    let left = inputRect.left + scrollLeft;
  
    // Check if picker would go off screen bottom
    const pickerHeight = 300; // Approximate height of picker
    if (top + pickerHeight > window.innerHeight + scrollTop) {
      // Position above input
      top = inputRect.top + scrollTop - pickerHeight;
    }
  
    // Check if picker would go off screen right
    const pickerWidth = 280; // Approximate width of picker
    if (left + pickerWidth > window.innerWidth + scrollLeft) {
      // Align to right edge of input
      left = inputRect.right + scrollLeft - pickerWidth;
    }
  
    this.pickerContainer.style.position = 'absolute';
    this.pickerContainer.style.top = `${top}px`;
    this.pickerContainer.style.left = `${left}px`;
    this.pickerContainer.style.zIndex = '1000';
    
    // Ensure picker is added to document body for proper positioning
    document.body.appendChild(this.pickerContainer);

    }

  /**
   * Get selected range
   * @returns {Object|null} Selected range
   */
  getRange() {
    const dateRange = this.dateRangePicker.getRange();
    const startTime = this.startTimePicker.getTime();
    const endTime = this.endTimePicker.getTime();

    if (!dateRange) return null;

    return {
      start: {
        date: dateRange.start,
        time: startTime || { hour: 0, minute: 0 },
      },
      end: {
        date: dateRange.end,
        time: endTime || { hour: 23, minute: 59 },
      },
    };
  }

  /**
   * Set range
   * @param {Object} range - Range object
   */
  setRange(range) {
    if (!range || !range.start || !range.end) {
      throw new Error("Invalid range");
    }

    // Set date range
    this.dateRangePicker.setRange({
      start: range.start.date,
      end: range.end.date,
    });

    // Set time range
    if (range.start.time) {
      this.startTimePicker.setTime(range.start.time);
    }

    if (range.end.time) {
      this.endTimePicker.setTime(range.end.time);
    }

    this._updateValue();
  }

  /**
   * Clear selection
   */
  clear() {
    this.dateRangePicker.clear();
    this.startTimePicker.clear();
    this.endTimePicker.clear();

    if (this.input) {
      this.input.value = "";
    }

    if (this.options.onChange) {
      this.options.onChange(null);
    }
  }

  /**
   * Destroy the picker
   */
  destroy() {
    if (this.dateRangePicker) {
      this.dateRangePicker.destroy();
    }

    if (this.startTimePicker) {
      this.startTimePicker.destroy();
    }

    if (this.endTimePicker) {
      this.endTimePicker.destroy();
    }

    if (this.container) {
      this.container.innerHTML = "";
      this.container.classList.remove("ndtp-datetime-range-picker");
    }

    this.initialized = false;
  }

  /**
   * Check if picker is visible
   * @returns {boolean}
   */
  isOpen() {
    return this.isVisible;
  }
}

export default DateTimeRangePicker;
